import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToolbarModule } from 'primeng/toolbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import {
  ComplaintService,
  ComplaintModel,
  ComplaintTypeModel,
} from '../../services/complaint.service';
import {
  ComplaintStatusService,
  ComplaintStatusModel,
} from '../../services/complaint-status.service';
import { ComplaintTypeService } from '../../services/complaint-type.service';

/** Severidades disponíveis no p-tag do PrimeNG */
type TagSeverity =
  | 'success'
  | 'info'
  | 'warn'
  | 'danger'
  | 'secondary'
  | 'contrast';

const TAG_PALETTE: TagSeverity[] = [
  'success',
  'info',
  'warn',
  'danger',
  'secondary',
  'contrast',
];

@Component({
  selector: 'app-complaint',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TagModule,
    ToolbarModule,
    ConfirmDialogModule,
    ToastModule,
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <p-toolbar>
          <ng-template pTemplate="start">
            <h2>Lista de Denúncias</h2>
          </ng-template>
          <ng-template pTemplate="end">
            <p-button
              label="Recarregar"
              icon="pi pi-refresh"
              (onClick)="loadComplaints()"
              [outlined]="true"
              severity="secondary"
              size="small"
              class="mr-2"
            >
            </p-button>
            <p-button
              label="Nova Denúncia"
              icon="pi pi-plus"
              (onClick)="newComplaint()"
              severity="primary"
            >
            </p-button>
          </ng-template>
        </p-toolbar>
      </ng-template>

      <ng-template pTemplate="content">
        <!-- Filtros -->
        <div class="filters-container p-3">
          <div class="filters-row">
            <span class="p-input-icon-left">
              <i class="pi pi-search"></i>
              <input
                pInputText
                type="text"
                placeholder="Buscar denúncia..."
                [(ngModel)]="searchTerm"
                (ngModelChange)="applyFilters()"
                class="w-full"
              />
            </span>

            <p-select
              [(ngModel)]="selectedTypeId"
              [options]="typeOptions"
              (onChange)="applyFilters()"
              placeholder="Todos os Tipos"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              class="w-full"
            >
            </p-select>

            <p-select
              [(ngModel)]="selectedStatusId"
              [options]="statusOptions"
              (onChange)="applyFilters()"
              placeholder="Todos os Status"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              class="w-full"
            >
            </p-select>
          </div>
        </div>

        <!-- Tabela -->
        <p-table
          [value]="filtered"
          [paginator]="true"
          [rows]="pageSize"
          [rowsPerPageOptions]="[5, 10, 20, 50]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="{first} - {last} de {totalRecords} registros"
          responsiveLayout="scroll"
          [loading]="isLoading"
          loadingIcon="pi pi-spin pi-spinner"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="id">
                ID <p-sortIcon field="id"></p-sortIcon>
              </th>
              <th pSortableColumn="complaintType.description">
                Tipo <p-sortIcon field="complaintType.description"></p-sortIcon>
              </th>
              <th pSortableColumn="complaintStatus.description">
                Status
                <p-sortIcon field="complaintStatus.description"></p-sortIcon>
              </th>
              <th pSortableColumn="address">
                Local <p-sortIcon field="address"></p-sortIcon>
              </th>
              <th pSortableColumn="registerDate">
                Data <p-sortIcon field="registerDate"></p-sortIcon>
              </th>
              <th style="width: 160px; min-width: 160px;">Ações</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-complaint>
            <tr>
              <td>{{ complaint.id }}</td>
              <td>
                <p-tag
                  [value]="complaint.complaintType?.description ?? '-'"
                  [severity]="getTypeSeverity(complaint)"
                ></p-tag>
              </td>
              <td>
                <p-tag
                  [value]="complaint.complaintStatus?.description ?? '-'"
                  [severity]="getStatusSeverity(complaint)"
                ></p-tag>
              </td>
              <td>{{ complaint.address ?? '-' }}</td>
              <td>{{ complaint.registerDate | date: 'dd/MM/yyyy' }}</td>
              <td>
                <div class="action-buttons">
                  <p-button
                    icon="pi pi-eye"
                    [outlined]="true"
                    severity="info"
                    size="small"
                    title="Visualizar detalhes"
                    (onClick)="viewComplaint(complaint.id)"
                  >
                  </p-button>
                  <p-button
                    icon="pi pi-trash"
                    title="Excluir"
                    (onClick)="confirmSoftDelete(complaint)"
                    severity="danger"
                    [outlined]="true"
                    size="small"
                  >
                  </p-button>
                  <!-- só visível para admin -->
                  <ng-container *ngIf="isAdmin()">
                    <p-button
                      icon="pi pi-trash"
                      title="Excluir Permanentemente"
                      (onClick)="confirmDelete(complaint)"
                      severity="danger"
                      [outlined]="true"
                      size="small"
                    >
                    </p-button>
                  </ng-container>
                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="8" class="text-center p-4">
                <i class="pi pi-info-circle text-4xl text-400 mb-3"></i>
                <p class="text-600">Nenhuma denúncia encontrada</p>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </ng-template>
    </p-card>

    <p-confirmDialog
      [acceptLabel]="'Sim'"
      [rejectLabel]="'Não'"
      acceptButtonStyleClass="p-button-success"
      rejectButtonStyleClass="p-button-secondary"
    >
    </p-confirmDialog>
    <p-toast></p-toast>
  `,
  styles: [
    `
      .filters-container {
        border-bottom: 1px solid #e0e0e0;
      }

      .filters-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
      }

      .filters-row input,
      .filters-row p-select {
        min-width: 220px;
        max-width: 300px;
      }

      .action-buttons {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        flex-wrap: nowrap;
      }

      :host ::ng-deep .p-toolbar {
        border: none;
        padding: 1rem 0;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 0;
      }

      :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
        padding: 0.5rem;
        vertical-align: middle;
      }
    `,
  ],
})
export class ComplaintComponent implements OnInit {
  complaints: ComplaintModel[] = [];
  filtered: ComplaintModel[] = [];
  searchTerm = '';
  selectedTypeId: number | null = null;
  selectedStatusId: number | null = null;
  pageSize = 10;
  isLoading = false;

  typeOptions: { label: string; value: number }[] = [];
  statusOptions: { label: string; value: number }[] = [];

  /** Cache determinístico: id → cor, populado conforme os dados chegam */
  private typeColorMap = new Map<number, TagSeverity>();
  private statusColorMap = new Map<number, TagSeverity>();

  // Constantes para os filtros
  private readonly TIPO_VEICULOS_ABANDONADOS = 'veiculos abandonados';
  private readonly STATUS_AGUARDANDO_ANALISE = 'aguardando análise';

  constructor(
    private complaintService: ComplaintService,
    private complaintStatusService: ComplaintStatusService,
    private complaintTypeService: ComplaintTypeService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadFiltersAndInitialize();
  }

  /**
   * Carrega status e tipos, encontra os IDs dos filtros padrão,
   * e só então carrega as denúncias já filtradas.
   */
  loadFiltersAndInitialize(): void {
    // Carrega status e tipos em paralelo
    this.complaintStatusService.findAll().subscribe({
      next: (statuses) => {
        this.statusOptions = statuses.map(s => ({
          label: s.description ?? '',
          value: s.id!,
        }));
        this.assignColors(statuses, this.statusColorMap);

        // Encontra o ID de "aguardando análise"
        const pending = statuses.find(
          s => s.description?.toLowerCase() === this.STATUS_AGUARDANDO_ANALISE,
        );
        this.selectedStatusId = pending?.id ?? null;

        // Agora carrega os tipos
        this.loadTypesAndComplaints();
      },
      error: (err) => {
        console.error('Erro ao carregar status:', err);
        this.loadTypesAndComplaints();
      },
    });
  }

  private loadTypesAndComplaints(): void {
    this.complaintTypeService.findAll().subscribe({
      next: (types) => {
        this.typeOptions = types.map(t => ({
          label: t.description ?? '',
          value: t.id!,
        }));

        // Encontra o ID de "veiculos abandonados"
        const abandoned = types.find(
          t => t.description?.toLowerCase() === this.TIPO_VEICULOS_ABANDONADOS,
        );
        this.selectedTypeId = abandoned?.id ?? null;

        // Agora carrega as denúncias com os filtros já definidos
        this.loadComplaints();
      },
      error: (err) => {
        console.error('Erro ao carregar tipos:', err);
        this.loadComplaints();
      },
    });
  }

  loadComplaints(filters?: {
    address?: string;
    neighborhood?: string;
    complaintType?: number | string;
    complaintStatus?: number | string;
  }): void {
    this.isLoading = true;

    // Envia os IDs (números) — o backend espera os IDs, não as descrições
    const typeId = filters?.complaintType ?? this.selectedTypeId ?? undefined;
    const statusId = filters?.complaintStatus ?? this.selectedStatusId ?? undefined;

    this.complaintService
      .findAllFiltered(
        filters?.address,
        filters?.neighborhood,
        typeId != null ? +typeId : undefined,
        statusId != null ? +statusId : undefined,
      )
      .subscribe({
        next: (data) => {
          this.complaints = data;
          this.buildTypeOptions(data);
          this.applyFilters();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Erro ao carregar denúncias:', err);
          const backendMsg =
            err.error?.message || err.error?.error || err.message || 'Erro desconhecido';
          this.messageService.add({
            severity: 'error',
            summary: `Erro HTTP ${err.status}`,
            detail: backendMsg,
            life: 8000,
          });
          this.isLoading = false;
        },
      });
  }

  private buildTypeOptions(data: ComplaintModel[]): void {
    const seen = new Map<number, string>();
    data.forEach((c) => {
      const t: ComplaintTypeModel | undefined = c['complaintType'];
      if (t?.id != null && !seen.has(t.id)) {
        seen.set(t.id, t.description);
      }
    });
    this.typeOptions = Array.from(seen.entries()).map(([value, label]) => ({
      label,
      value,
    }));
    this.assignColors(data, this.typeColorMap, 'complaintType');
  }

  /**
   * Atribui uma cor fixa por id seguindo a paleta circularmente.
   * Mantém cores já atribuídas para não trocar durante o uso da tela.
   */
  private assignColors(
    items: any[],
    map: Map<number, TagSeverity>,
    key: string = 'id',
  ): void {
    let nextIdx = map.size;
    items.forEach((item) => {
      const id =
        key === 'complaintType'
          ? (item as any)['complaintType']?.id
          : ((item as any)[key] ?? item['id']);
      if (id != null && !map.has(id)) {
        map.set(id, TAG_PALETTE[nextIdx % TAG_PALETTE.length]);
        nextIdx++;
      }
    });
  }

  /** Retorna a severidade do p-tag para um tipo de denúncia */
  getTypeSeverity(complaint: ComplaintModel): TagSeverity {
    const id = complaint.complaintType?.id;
    return id != null ? (this.typeColorMap.get(id) ?? 'info') : 'info';
  }

  /** Retorna a severidade do p-tag para um status de denúncia */
  getStatusSeverity(complaint: ComplaintModel): TagSeverity {
    const id = complaint.complaintStatus?.id;
    return id != null ? (this.statusColorMap.get(id) ?? 'warn') : 'warn';
  }

  applyFilters(): void {
    let result = this.complaints;

    // Filtro por tipo (se selecionado)
    if (this.selectedTypeId != null) {
      result = result.filter(
        (c) => c['complaintType']?.id === this.selectedTypeId,
      );
    }

    // Filtro por status (se selecionado)
    if (this.selectedStatusId != null) {
      result = result.filter(
        (c) => c['complaintStatus']?.id === this.selectedStatusId,
      );
    }

    // Filtro por busca textual
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          String(c.id ?? '').includes(term) ||
          (c.text ?? '').toLowerCase().includes(term) ||
          (c.complaintType?.description ?? '').toLowerCase().includes(term) ||
          (c.address ?? '').toLowerCase().includes(term),
      );
    }

    this.filtered = result;
  }

  /**
   * Limpa todos os filtros e recarrega todas as denúncias
   */
  clearFilters(): void {
    this.selectedTypeId = null;
    this.selectedStatusId = null;
    this.searchTerm = '';
    this.loadComplaints();
  }

  newComplaint(): void {
    this.router.navigate(['/home/complaints/new']);
  }

  viewComplaint(id: number): void {
    this.router.navigate(['/home/complaints/details', id]);
  }

  confirmSoftDelete(complaint: ComplaintModel): void {
    this.confirmationService.confirm({
      message: `Deseja excluir a denúncia #${complaint['id']}?`,
      header: 'Confirmar exclusão',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.softDeleteComplaint(complaint['id']!),
    });
  }

  private softDeleteComplaint(id: number): void {
    this.complaintService.softDeleteById(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Denúncia excluída.',
        });
        this.loadComplaints();
      },
      error: (err) => {
        console.error('Erro ao excluir:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível excluir a denúncia.',
        });
      },
    });
  }

  confirmDelete(complaint: ComplaintModel): void {
    this.confirmationService.confirm({
      message: `Deseja excluir permanentemente a denúncia #${complaint['id']}?`,
      header: 'Confirmar exclusão permanente',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteComplaint(complaint['id']!),
    });
  }

  private deleteComplaint(id: number): void {
    this.complaintService.deleteById(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Denúncia excluída permanentemente.',
        });
        this.loadComplaints();
      },
      error: (err) => {
        console.error('Erro ao excluir permanentemente:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível excluir a denúncia permanentemente.',
        });
      },
    });
  }

  isAdmin(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.includes('ROLE_ADMIN');
  }
}